import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css"; // custom styles
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WithdrawalModal from "./WithdrawalModal";
import DepositModal from "./DepositModal"; // Import the modal
import CommissionModal from "./CommissionModal";
import { 
  FaMoneyBillWave, 
  FaArrowDown, 
  FaChartLine, 
  FaGem, 
  FaUniversity, 
  FaChartBar, 
  FaCreditCard, 
  FaUsers, 
  FaUserTie,
  FaUserFriends,
  FaHandshake,
  FaLayerGroup,
  FaWallet,
  FaExchangeAlt,
  FaBalanceScale
} from "react-icons/fa";

export default function Dashboard() {
  const navigate = useNavigate();
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [commissionRequests, setCommissionRequests] = useState([]);
  const [copyTradingRequests, setCopyTradingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const [copyTradingLoading, setCopyTradingLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({});
  const [adminRemark, setAdminRemark] = useState({});
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [totals, setTotals] = useState({
  total_deposits: 0,
  total_withdraws: 0,
  net_deposit: 0,
  total_equity: 0,
  total_credit: 0,
  credit_used: 0,
  withdrawable_equity: 0,
});
const [users, setUsers] = useState({
  total_users: 0,
  new_users_24h: 0,
  new_users_7d: 0,
  kyc_verified: 0,
  kyc_unverified: 0,
  email_verified: 0,
  email_unverified: 0,
});
const [deposits, setDeposits] = useState({
  pending: 0,
  approved: 0,
  rejected: 0,
});
const [withdrawals, setWithdrawals] = useState({
  pending: 0,
  approved: 0,
  rejected: 0,
});
const [totalClients, setTotalClients] = useState(0);
const [totalTradingAccounts, setTotalTradingAccounts] = useState(0);
const [totalTransactions, setTotalTransactions] = useState(0);
const [totalIbs, setTotalIbs] = useState(0);

useEffect(() => {
  fetchDashboardStats();
  fetchPendingDeposits();
  fetchPendingWithdrawals();
  fetchCommissionRequests();
  fetchCopyTradingRequests();
}, []);

const fetchDashboardStats = async () => {
  try {
    const res = await axios.get("https://server.tradefxonline.com/admin_dashboard.php");
    if (res.data.success) {
      setTotals(res.data.totals);
      setUsers(res.data.users);
      setDeposits(res.data.deposits);
      setWithdrawals(res.data.withdrawals);
      setTotalClients(res.data.total_clients);
      setTotalTradingAccounts(res.data.total_trading_accounts);
      setTotalTransactions(res.data.total_transactions);
      setTotalIbs(res.data.total_ibs);
    }
  } catch (err) {
    console.error("Failed to fetch dashboard stats:", err);
    toast.error("Failed to load dashboard stats");
  }
};

const fetchCommissionRequests = async () => {
  try {
    setCommissionLoading(true);
    const response = await fetch("https://server.tradefxonline.com/admin_ib_withdraw.php");
    const data = await response.json();
    
    if (data.success) {
      setCommissionRequests(data.requests);
    } else {
      console.error("Failed to fetch commission requests:", data.message);
      toast.error(data.message || "Failed to fetch commission requests");
    }
  } catch (err) {
    console.error("Error fetching commission requests:", err);
    toast.error("Error fetching commission requests: " + err.message);
  } finally {
    setCommissionLoading(false);
  }
};

const fetchCopyTradingRequests = async () => {
  try {
    setCopyTradingLoading(true);
    const response = await fetch("https://server.tradefxonline.com/admin_fetch_copyreq.php");
    const data = await response.json();
    
    if (data.status === "success") {
      setCopyTradingRequests(data.data);
    } else {
      console.error("Failed to fetch copy trading requests:", data.message);
      toast.error(data.message || "Failed to fetch copy trading requests");
    }
  } catch (err) {
    console.error("Error fetching copy trading requests:", err);
    toast.error("Error fetching copy trading requests: " + err.message);
  } finally {
    setCopyTradingLoading(false);
  }
};

  const fetchPendingWithdrawals = async () => {
    try {
      const res = await axios.post(
        `https://server.tradefxonline.com/admin_withdrawal.php`,
        { action: "fetch_pending_withdrawals" },
        { 
          headers: { 
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          }
        }
      );
      if (res.data.success) {
        setPendingWithdrawals(res.data.withdrawals);
      } else {
        console.error("API Error:", res.data.error);
        toast.error(res.data.error || "Failed to fetch withdrawals");
      }
    } catch (err) {
      console.error("Failed to fetch pending withdrawals:", err.response?.data || err.message);
      toast.error("Failed to fetch withdrawals: " + (err.response?.data?.error || err.message));
    }
  };

  const fetchPendingDeposits = async () => {
    try {
      const res = await axios.post(
        `https://server.tradefxonline.com/admin_deposit.php`,
        { action: "fetch_pending_deposits" },
        { 
          headers: { 
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          // withCredentials: true
        }
      );
      console.log("API Response:", res.data); // Debug response
      if (res.data.success) {
        setPendingDeposits(res.data.deposits);
        console.log("Deposits loaded:", res.data.deposits); // Debug deposits
      } else {
        console.error("API Error:", res.data.error);
        toast.error(res.data.error || "Failed to fetch deposits");
      }
    } catch (err) {
      console.error("Failed to fetch pending deposits:", err.response?.data || err.message);
      toast.error("Failed to fetch deposits: " + (err.response?.data?.error || err.message));
    }
  };

  const handleDeposit = async (requestId, action, adminComment = "") => {
    try {
      console.log(`Setting ${action} loading for deposit:`, requestId);
      setButtonLoading(prev => {
        const newState = { ...prev, [`${requestId}_deposit_${action}`]: true };
        console.log("New buttonLoading state:", newState);
        return newState;
      });

      const res = await axios.post(
        `https://server.tradefxonline.com/admin_deposit.php`,
        {
          action,
          request_id: requestId,
          admin_comment: adminComment,
        },
        { 
          headers: { 
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          withCredentials: true
        }
      );
      if (res.data.success) {
        if (action === 'reject') {
          toast.error(`Deposit ${action}ed successfully`, {
            style: {
              backgroundColor: '#dc2626',
              color: 'white'
            }
          });
        } else {
          toast.success(`Deposit ${action}ed successfully`);
        }
        fetchPendingDeposits(); // Refresh list
      } else {
        toast.error(res.data.error || `Failed to ${action} deposit`);
      }
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      console.log(`Clearing ${action} loading for deposit:`, requestId);
      setButtonLoading(prev => {
        const newState = { ...prev, [`${requestId}_deposit_${action}`]: false };
        console.log("Final buttonLoading state:", newState);
        return newState;
      });
    }
  };

  const handleWithdrawal = async (requestId, action, adminComment = "") => {
  try {
    console.log(`Setting ${action} loading for withdrawal:`, requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_withdrawal_${action}`]: true };
      console.log("New buttonLoading state:", newState);
      return newState;
    });

    const res = await axios.post(
      `https://server.tradefxonline.com/admin_withdrawal.php`,
      {
        action,
        request_id: requestId,
        admin_comment: adminComment,
      },
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        // withCredentials: true
      }
    );

    if (res.data.success) {
      if (action === 'reject') {
        toast.error(`Withdrawal ${action}ed successfully`, {
          style: {
            backgroundColor: '#dc2626',
            color: 'white'
          }
        });
      } else {
        toast.success(`Withdrawal ${action}ed successfully`);
      }
      fetchPendingWithdrawals(); // Refresh list
    } else {
      toast.error(res.data.error || `Failed to ${action} withdrawal`);
    }
  } catch (err) {
    toast.error(`Error: ${err.message}`);
  } finally {
    console.log(`Clearing ${action} loading for withdrawal:`, requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_withdrawal_${action}`]: false };
      console.log("Final buttonLoading state:", newState);
      return newState;
    });
  }
};

// Handle commission request approve action
const handleCommissionApprove = async (requestId, remark = null) => {
  const adminRemarkText = remark || adminRemark[requestId] || "";
  
  if (!adminRemarkText.trim()) {
    toast.error("Please enter an admin remark before proceeding.");
    return;
  }

  try {
    console.log("Setting approve loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_approve`]: true };
      console.log("New buttonLoading state:", newState);
      return newState;
    });
    
    const response = await fetch("https://server.tradefxonline.com/admin_ib_withdraw.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        request_id: requestId,
        action: "approve",
        admin_remark: adminRemarkText,
      }),
    });

    const data = await response.json();
    
    if (data.success || response.ok) {
      toast.success("Commission request approved successfully!");
      // Remove the processed request from the list
      setCommissionRequests(prev => prev.filter(req => req.id !== requestId));
      // Clear the remark
      setAdminRemark(prev => ({ ...prev, [requestId]: "" }));
      // Close modal if open
      setSelectedCommission(null);
    } else {
      toast.error(data.message || "Failed to approve commission request");
    }
  } catch (err) {
    toast.error("Error approving commission request: " + err.message);
  } finally {
    console.log("Clearing approve loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_approve`]: false };
      console.log("Final buttonLoading state:", newState);
      return newState;
    });
  }
};

// Handle commission request reject action
const handleCommissionReject = async (requestId, remark = null) => {
  const adminRemarkText = remark || adminRemark[requestId] || "";
  
  if (!adminRemarkText.trim()) {
    toast.error("Please enter an admin remark before proceeding.");
    return;
  }

  try {
    console.log("Setting reject loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_reject`]: true };
      console.log("New buttonLoading state:", newState);
      return newState;
    });
    
    const response = await fetch("https://server.tradefxonline.com/admin_ib_withdraw.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        request_id: requestId,
        action: "reject",
        admin_remark: adminRemarkText,
      }),
    });

    const data = await response.json();
    
    if (data.success || response.ok) {
      toast.success("Commission request rejected successfully!");
      // Remove the processed request from the list
      setCommissionRequests(prev => prev.filter(req => req.id !== requestId));
      // Clear the remark
      setAdminRemark(prev => ({ ...prev, [requestId]: "" }));
      // Close modal if open
      setSelectedCommission(null);
    } else {
      toast.error(data.message || "Failed to reject commission request");
    }
  } catch (err) {
    toast.error("Error rejecting commission request: " + err.message);
  } finally {
    console.log("Clearing reject loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_reject`]: false };
      console.log("Final buttonLoading state:", newState);
      return newState;
    });
  }
};

// Handle copy trading request approve action
const handleCopyTradingApprove = async (requestId) => {
  try {
    console.log("Setting copy trading approve loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_copy_approve`]: true };
      console.log("New buttonLoading state:", newState);
      return newState;
    });
    
    const response = await fetch("https://server.tradefxonline.com/admin_createinv_aprv_rej.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        request_id: requestId,
        action: "approve",
      }),
    });

    const data = await response.json();
    
    if (data.status === "success" || response.ok) {
      toast.success("Copy trading request approved successfully!");
      // Remove the processed request from the list
      setCopyTradingRequests(prev => prev.filter(req => req.id !== requestId));
    } else {
      toast.error(data.message || "Failed to approve copy trading request");
    }
  } catch (err) {
    toast.error("Error approving copy trading request: " + err.message);
  } finally {
    console.log("Clearing copy trading approve loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_copy_approve`]: false };
      console.log("Final buttonLoading state:", newState);
      return newState;
    });
  }
};

// Handle copy trading request reject action
const handleCopyTradingReject = async (requestId) => {
  try {
    console.log("Setting copy trading reject loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_copy_reject`]: true };
      console.log("New buttonLoading state:", newState);
      return newState;
    });
    
    const response = await fetch("https://server.tradefxonline.com/admin_createinv_aprv_rej.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        request_id: requestId,
        action: "reject",
      }),
    });

    const data = await response.json();
    
    if (data.status === "success" || response.ok) {
      toast.success("Copy trading request rejected successfully!");
      // Remove the processed request from the list
      setCopyTradingRequests(prev => prev.filter(req => req.id !== requestId));
    } else {
      toast.error(data.message || "Failed to reject copy trading request");
    }
  } catch (err) {
    toast.error("Error rejecting copy trading request: " + err.message);
  } finally {
    console.log("Clearing copy trading reject loading for:", requestId);
    setButtonLoading(prev => {
      const newState = { ...prev, [`${requestId}_copy_reject`]: false };
      console.log("Final buttonLoading state:", newState);
      return newState;
    });
  }
};

// Handle remark input change
const handleRemarkChange = (requestId, value) => {
  setAdminRemark(prev => ({ ...prev, [requestId]: value }));
};

// Handle copy wallet address to clipboard
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Wallet address copied to clipboard!");
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success("Wallet address copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy wallet address");
    }
    document.body.removeChild(textArea);
  }
};


  return (
    <div className="dashboard">
      <ToastContainer position="top-right" autoClose={3000} />
      {selectedDeposit && (
        <DepositModal 
          deposit={selectedDeposit} 
          onClose={() => setSelectedDeposit(null)}
          onAction={handleDeposit}
        />
      )}

      {selectedCommission && (
        <CommissionModal 
          commission={selectedCommission} 
          onClose={() => setSelectedCommission(null)}
          onApprove={handleCommissionApprove}
          onReject={handleCommissionReject}
        />
      )}

      {/* Stats Row 1 */}
      <div className="row three">
        <StatCard 
          title="Total Deposits" 
          value={`$${totals.total_deposits || 0}`} 
          icon={<FaMoneyBillWave />}
          onClick={() => navigate('/deposit-history')}
        />
        <StatCard 
          title="Total Withdrawals" 
          value={`$${totals.total_withdraws || 0}`} 
          icon={<FaArrowDown />}
          onClick={() => navigate('/withdraw-history')}
        />
        <StatCard 
          title="Net Deposit" 
          value={`$${totals.net_deposit || 0}`} 
          icon={<FaChartLine />}
          onClick={() => navigate('/account-ledger')}
        />
      </div>

      {/* Stats Row 2 */}
      <div className="row three">
        <StatCard 
          title="Total Equity" 
          value={`$${totals.total_equity || 0}`} 
          icon={<FaGem />}
          onClick={() => navigate('/clients')}
        />
        <StatCard 
          title="Total Credit" 
          value={`$${totals.total_credit || 0}`} 
          icon={<FaUniversity />}
          onClick={() => navigate('/credit-in-out')}
        />
        <StatCard 
          title="Credit Used" 
          value={`$${totals.credit_used || 0}`} 
          icon={<FaChartBar />}
          onClick={() => navigate('/credit-in-out')}
        />
      </div>

      {/* Stats Row 3 */}
      <div className="row three">
        <StatCard 
          title="Withdrawable Equity" 
          value={`$${totals.withdrawable_equity || 0}`} 
          icon={<FaCreditCard />}
          onClick={() => navigate('/clients')}
        />
        <StatCard 
          title="Total Clients" 
          value={totalClients || 0} 
          icon={<FaUsers />}
          onClick={() => navigate('/clients')}
        />
        <StatCard 
          title="Trading Accounts" 
          value={totalTradingAccounts || 0} 
          icon={<FaUserTie />}
          onClick={() => navigate('/approved-accounts')}
        />
      </div>

      {/* Profit Section */}
      {/* <div className="card profit">
        <p>Company P/L by Net Deposit $978.19</p>
        <h2 className="red">- $0</h2>
        <p className="muted">Last updated 12 hours ago</p>
      </div> */}


     

      {/* User Statistics */}
      <div className="row three">
        <StatCard title="Total Users" value={users.total_users} icon={<FaUsers />} onClick={() => navigate('/clients')} />
        <StatCard title="New Users (24h)" value={users.new_users_24h} icon={<FaUserTie />} onClick={() => navigate('/clients')} />
        <StatCard title="New Users (7d)" value={users.new_users_7d} icon={<FaUserFriends />} onClick={() => navigate('/clients')} />
      </div>

      {/* KYC & Email Status */}
      <div className="row three">
        <StatCard title="KYC Verified" value={users.kyc_verified} icon={<FaHandshake />} onClick={() => navigate('/kyc-documents')} />
        <StatCard title="KYC Unverified" value={users.kyc_unverified} icon={<FaLayerGroup />} onClick={() => navigate('/kyc-documents')} />
        <StatCard title="Email Verified" value={users.email_verified} icon={<FaWallet />} onClick={() => navigate('/clients')} />
      </div>

      {/* Email & Trading */}
      <div className="row three">
        <StatCard title="Email Unverified" value={users.email_unverified} icon={<FaExchangeAlt />} onClick={() => navigate('/clients')} />
        <StatCard title="Total Trading Accounts" value={totalTradingAccounts} icon={<FaLayerGroup />} onClick={() => navigate('/approved-accounts')} />
        <StatCard title="Total Transactions" value={totalTransactions} icon={<FaExchangeAlt />} onClick={() => navigate('/account-ledger')} />
      </div>

      {/* Deposit Status */}
      <div className="row three">
        <StatCard title="Pending Deposits" value={deposits.pending} icon={<FaMoneyBillWave />} onClick={() => navigate('/deposit-history')} />
        <StatCard title="Approved Deposits" value={deposits.approved} icon={<FaChartLine />} onClick={() => navigate('/deposit-history')} />
        <StatCard title="Rejected Deposits" value={deposits.rejected} icon={<FaArrowDown />} onClick={() => navigate('/rejected-deposits')} />
      </div>

      {/* Withdrawal Status */}
      <div className="row three">
        <StatCard title="Pending Withdrawals" value={withdrawals.pending} icon={<FaGem />} onClick={() => navigate('/withdraw-history')} />
        <StatCard title="Approved Withdrawals" value={withdrawals.approved} icon={<FaUniversity />} onClick={() => navigate('/withdraw-history')} />
        <StatCard title="Rejected Withdrawals" value={withdrawals.rejected} icon={<FaChartBar />} onClick={() => navigate('/rejected-withdrawals')} />
      </div>


      {/* Pending Deposits */}
      <div className="col-span-12 lg:col-span-12">
  <Card className="bg-gradient-to-br from-white via-pink-50 to-pink-100 backdrop-blur-md">
    <CardHeader>
      <CardTitle>Pending Deposits History</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" >
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Trade ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Transaction ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Deposit Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pendingDeposits.filter(deposit => deposit.status === 'pending').length > 0 ? (
              pendingDeposits.filter(deposit => deposit.status === 'pending').map((deposit) => (
                <tr key={deposit.id}>
                  <td className="px-6 py-4 text-sm text-gray-900">{deposit.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{deposit.trade_id}</td>
                  <td className="px-6 py-4 text-green-600 font-medium">${deposit.amount}</td>
                  <td className="px-6 py-4 capitalize">{deposit.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{deposit.transaction_id}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(deposit.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 rounded-full text-white text-xs bg-yellow-500">
                      {deposit.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {deposit.deposit_address ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                          {deposit.deposit_address.length > 20
                            ? `${deposit.deposit_address.substring(0, 20)}...`
                            : deposit.deposit_address}
                        </span>
                        <button
                          onClick={() => copyToClipboard(deposit.deposit_address)}
                          className="text-blue-500 hover:text-blue-700 text-xs"
                          title="Copy deposit address"
                        >
                          📋
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => handleDeposit(deposit.id, "approve")}
                        disabled={buttonLoading[`${deposit.id}_deposit_approve`] || buttonLoading[`${deposit.id}_deposit_reject`]}
                        className={`bg-green-500 hover:bg-green-600 deposit-approve-btn ${buttonLoading[`${deposit.id}_deposit_approve`] ? 'loading' : ''}`}
                        style={{
                          opacity: buttonLoading[`${deposit.id}_deposit_approve`] ? 0.7 : 1,
                          cursor: buttonLoading[`${deposit.id}_deposit_approve`] ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {buttonLoading[`${deposit.id}_deposit_approve`] ? (
                          <>
                            <span className="commission-spinner" style={{
                              display: 'inline-block',
                              width: '12px',
                              height: '12px',
                              border: '2px solid #ffffff',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              marginRight: '5px'
                            }}></span>
                            Approving...
                          </>
                        ) : (
                          "Approve"
                        )}
                      </Button>
                      <Button
                        onClick={() => handleDeposit(deposit.id, "reject")}
                        disabled={buttonLoading[`${deposit.id}_deposit_approve`] || buttonLoading[`${deposit.id}_deposit_reject`]}
                        className={`bg-red-500 hover:bg-red-600 deposit-reject-btn ${buttonLoading[`${deposit.id}_deposit_reject`] ? 'loading' : ''}`}
                        style={{
                          opacity: buttonLoading[`${deposit.id}_deposit_reject`] ? 0.7 : 1,
                          cursor: buttonLoading[`${deposit.id}_deposit_reject`] ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {buttonLoading[`${deposit.id}_deposit_reject`] ? (
                          <>
                            <span className="commission-spinner" style={{
                              display: 'inline-block',
                              width: '12px',
                              height: '12px',
                              border: '2px solid #ffffff',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              marginRight: '5px'
                            }}></span>
                            Rejecting...
                          </>
                        ) : (
                          "Reject"
                        )}
                      </Button>
                      <Button
                        onClick={() => setSelectedDeposit(deposit)}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        View
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                  No pending deposits found
                </td>
              </tr>
            )}
            </tbody>
          </table>
          </div>
        </div>
    </CardContent>
  </Card>
</div>


      {/* Pending Withdrawals */}
     <div className="col-span-12 lg:col-span-12">
  <Card className="bg-gradient-to-br from-white via-pink-50 to-pink-100 backdrop-blur-md">
    <CardHeader>
      <CardTitle>Pending Withdrawals History</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{maxHeight: '320px'}}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trade ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wallet Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Array.isArray(pendingWithdrawals) && pendingWithdrawals.length > 0 ? (
                pendingWithdrawals
                  .filter((withdrawal) => withdrawal?.status === 'pending')
                  .map((withdrawal) => (
                    <tr key={withdrawal?.id || Math.random()}>
                      <td className="px-6 py-4 text-sm text-gray-900">{withdrawal?.email || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{withdrawal?.trade_id || 'N/A'}</td>
                      <td className="px-6 py-4 text-red-600 font-medium">${withdrawal?.amount || '0.00'}</td>
                      <td className="px-6 py-4 capitalize">{withdrawal?.type || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <span className="font-medium text-blue-600 capitalize">
                          {withdrawal?.payout_method || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {withdrawal?.wallet_address ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-600 font-mono bg-gray-100 px-2 py-1 rounded">
                              {withdrawal.wallet_address.length > 20
                                ? `${withdrawal.wallet_address.substring(0, 20)}...`
                                : withdrawal.wallet_address}
                            </span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(withdrawal.wallet_address);
                                toast.success('Wallet address copied to clipboard!');
                              }}
                              className="text-blue-500 hover:text-blue-700 text-xs"
                              title="Copy wallet address"
                            >
                              📋
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{withdrawal?.comment || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {withdrawal?.created_at
                          ? new Date(withdrawal.created_at).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded-full text-white text-xs bg-yellow-500">
                          {withdrawal?.status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleWithdrawal(withdrawal?.id, 'approve')}
                            disabled={
                              buttonLoading[`${withdrawal?.id}_withdrawal_approve`] ||
                              buttonLoading[`${withdrawal?.id}_withdrawal_reject`]
                            }
                            className={`bg-green-500 hover:bg-green-600 withdrawal-approve-btn ${
                              buttonLoading[`${withdrawal?.id}_withdrawal_approve`] ? 'loading' : ''
                            }`}
                            style={{
                              opacity: buttonLoading[`${withdrawal?.id}_withdrawal_approve`] ? 0.7 : 1,
                              cursor: buttonLoading[`${withdrawal?.id}_withdrawal_approve`]
                                ? 'not-allowed'
                                : 'pointer',
                            }}
                          >
                            {buttonLoading[`${withdrawal?.id}_withdrawal_approve`] ? (
                              <>
                                <span
                                  className="commission-spinner"
                                  style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid #ffffff',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '5px',
                                  }}
                                ></span>
                                Approving...
                              </>
                            ) : (
                              'Approve'
                            )}
                          </Button>
                          <Button
                            onClick={() => handleWithdrawal(withdrawal?.id, 'reject')}
                            disabled={
                              buttonLoading[`${withdrawal?.id}_withdrawal_approve`] ||
                              buttonLoading[`${withdrawal?.id}_withdrawal_reject`]
                            }
                            className={`bg-red-500 hover:bg-red-600 withdrawal-reject-btn ${
                              buttonLoading[`${withdrawal?.id}_withdrawal_reject`] ? 'loading' : ''
                            }`}
                            style={{
                              opacity: buttonLoading[`${withdrawal?.id}_withdrawal_reject`] ? 0.7 : 1,
                              cursor: buttonLoading[`${withdrawal?.id}_withdrawal_reject`]
                                ? 'not-allowed'
                                : 'pointer',
                            }}
                          >
                            {buttonLoading[`${withdrawal?.id}_withdrawal_reject`] ? (
                              <>
                                <span
                                  className="commission-spinner"
                                  style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid #ffffff',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '5px',
                                  }}
                                ></span>
                                Rejecting...
                              </>
                            ) : (
                              'Reject'
                            )}
                          </Button>
                          <Button
                            onClick={() => setSelectedWithdrawal(withdrawal)}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    No pending withdrawals found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </CardContent>
  </Card>
</div>
      {selectedWithdrawal && (
        <WithdrawalModal
          withdrawal={selectedWithdrawal}
          onClose={() => setSelectedWithdrawal(null)}
          onAction={handleWithdrawal}
        />
      )}

      {/* IB Commission Requests */}
     <div className="col-span-12 lg:col-span-12">
  <Card className="bg-gradient-to-br from-white via-pink-50 to-pink-100 backdrop-blur-md">
    <CardHeader>
      <CardTitle>IB Commission Withdrawal Requests</CardTitle>
    </CardHeader>
    <CardContent>
      {commissionLoading ? (
        <div className="text-center py-8 text-gray-500">
          Loading commission requests...
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{maxHeight: '320px'}}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Commission Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Wallet Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Network
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.isArray(commissionRequests) && commissionRequests.length > 0 ? (
                  commissionRequests.map((request) => (
                    <tr key={request?.id || Math.random()}>
                      <td className="px-6 py-4 text-sm text-gray-900">{request?.id || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{request?.email || 'N/A'}</td>
                      <td className="px-6 py-4 text-green-600 font-medium">
                        ${parseFloat(request?.total_commission || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 capitalize">{request?.type || 'N/A'}</td>
                      <td className="px-6 py-4">
                        {request?.walletaddress ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-900 max-w-32 truncate">
                              {request.walletaddress.length > 20
                                ? `${request.walletaddress.substring(0, 20)}...`
                                : request.walletaddress}
                            </span>
                            <button
                              onClick={() => copyToClipboard(request.walletaddress)}
                              className="text-blue-500 hover:text-blue-700 text-xs"
                              title="Copy wallet address"
                            >
                              📋
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{request?.network || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {request?.created_at
                          ? new Date(request.created_at).toLocaleString()
                          : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => setSelectedCommission(request)}
                            className="bg-blue-500 hover:bg-blue-600 text-xs px-3 py-1"
                          >
                            View
                          </Button>
                          <Button
                            onClick={() => handleCommissionApprove(request?.id)}
                            disabled={
                              buttonLoading[`${request?.id}_approve`] ||
                              buttonLoading[`${request?.id}_reject`]
                            }
                            className={`bg-green-500 hover:bg-green-600 text-xs px-3 py-1 commission-approve-btn ${
                              buttonLoading[`${request?.id}_approve`] ? 'loading' : ''
                            }`}
                            style={{
                              opacity: buttonLoading[`${request?.id}_approve`] ? 0.7 : 1,
                              cursor: buttonLoading[`${request?.id}_approve`] ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {buttonLoading[`${request?.id}_approve`] ? (
                              <>
                                <span
                                  className="commission-spinner"
                                  style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid #ffffff',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '5px',
                                  }}
                                ></span>
                                Approving...
                              </>
                            ) : (
                              'Approve'
                            )}
                          </Button>
                          <Button
                            onClick={() => handleCommissionReject(request?.id)}
                            disabled={
                              buttonLoading[`${request?.id}_approve`] ||
                              buttonLoading[`${request?.id}_reject`]
                            }
                            className={`bg-red-500 hover:bg-red-600 text-xs px-3 py-1 commission-reject-btn ${
                              buttonLoading[`${request?.id}_reject`] ? 'loading' : ''
                            }`}
                            style={{
                              opacity: buttonLoading[`${request?.id}_reject`] ? 0.7 : 1,
                              cursor: buttonLoading[`${request?.id}_reject`] ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {buttonLoading[`${request?.id}_reject`] ? (
                              <>
                                <span
                                  className="commission-spinner"
                                  style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid #ffffff',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '5px',
                                  }}
                                ></span>
                                Rejecting...
                              </>
                            ) : (
                              'Reject'
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                      No pending commission requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
</div>

      {/* Copy Trading Requests Section */}
      <div className="mb-8">
        <Card className="bg-gradient-to-br from-white via-pink-50 to-pink-100">
          <CardHeader>
            <CardTitle>Pending Copy Trading Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {copyTradingLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading copy trading requests...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="overflow-y-auto" style={{maxHeight: '320px'}}>
                  <table className="min-w-full divide-y divide-gray-200">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Master Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Master Trade ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Slave Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Slave Trade ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {copyTradingRequests.length > 0 ? (
                      copyTradingRequests.map((request) => (
                        <tr key={request.id}>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.id}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.email}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.mastername}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.mastertrade}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.slavename}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.slavetrade}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => handleCopyTradingApprove(request.id)}
                                disabled={buttonLoading[`${request.id}_copy_approve`] || buttonLoading[`${request.id}_copy_reject`]}
                                className={`bg-green-500 hover:bg-green-600 text-xs px-3 py-1 ${buttonLoading[`${request.id}_copy_approve`] ? 'loading' : ''}`}
                                style={{
                                  opacity: buttonLoading[`${request.id}_copy_approve`] ? 0.7 : 1,
                                  cursor: buttonLoading[`${request.id}_copy_approve`] ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {buttonLoading[`${request.id}_copy_approve`] ? (
                                  <>
                                    <span className="copy-trading-spinner" style={{
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      border: '2px solid #ffffff',
                                      borderTop: '2px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      marginRight: '5px'
                                    }}></span>
                                    Approving...
                                  </>
                                ) : (
                                  "Approve"
                                )}
                              </Button>
                              <Button
                                onClick={() => handleCopyTradingReject(request.id)}
                                disabled={buttonLoading[`${request.id}_copy_approve`] || buttonLoading[`${request.id}_copy_reject`]}
                                className={`bg-red-500 hover:bg-red-600 text-xs px-3 py-1 ${buttonLoading[`${request.id}_copy_reject`] ? 'loading' : ''}`}
                                style={{
                                  opacity: buttonLoading[`${request.id}_copy_reject`] ? 0.7 : 1,
                                  cursor: buttonLoading[`${request.id}_copy_reject`] ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {buttonLoading[`${request.id}_copy_reject`] ? (
                                  <>
                                    <span className="copy-trading-spinner" style={{
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      border: '2px solid #ffffff',
                                      borderTop: '2px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      marginRight: '5px'
                                    }}></span>
                                    Rejecting...
                                  </>
                                ) : (
                                  "Reject"
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                          No pending copy trading requests found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="mb-8">
        <Card className="bg-gradient-to-br from-white via-pink-50 to-pink-100">
          <CardHeader>
            <CardTitle>Pending PAMM Withdrawal Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {copyTradingLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading PAMM requests...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="overflow-y-auto" style={{maxHeight: '320px'}}>
                  <table className="min-w-full divide-y divide-gray-200">
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Master Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Master Trade ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Slave Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Slave Trade ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {copyTradingRequests.length > 0 ? (
                      copyTradingRequests.map((request) => (
                        <tr key={request.id}>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.id}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.email}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.mastername}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.mastertrade}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.slavename}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{request.slavetrade}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(request.created_at).toLocaleString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => handleCopyTradingApprove(request.id)}
                                disabled={buttonLoading[`${request.id}_copy_approve`] || buttonLoading[`${request.id}_copy_reject`]}
                                className={`bg-green-500 hover:bg-green-600 text-xs px-3 py-1 ${buttonLoading[`${request.id}_copy_approve`] ? 'loading' : ''}`}
                                style={{
                                  opacity: buttonLoading[`${request.id}_copy_approve`] ? 0.7 : 1,
                                  cursor: buttonLoading[`${request.id}_copy_approve`] ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {buttonLoading[`${request.id}_copy_approve`] ? (
                                  <>
                                    <span className="copy-trading-spinner" style={{
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      border: '2px solid #ffffff',
                                      borderTop: '2px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      marginRight: '5px'
                                    }}></span>
                                    Approving...
                                  </>
                                ) : (
                                  "Approve"
                                )}
                              </Button>
                              <Button
                                onClick={() => handleCopyTradingReject(request.id)}
                                disabled={buttonLoading[`${request.id}_copy_approve`] || buttonLoading[`${request.id}_copy_reject`]}
                                className={`bg-red-500 hover:bg-red-600 text-xs px-3 py-1 ${buttonLoading[`${request.id}_copy_reject`] ? 'loading' : ''}`}
                                style={{
                                  opacity: buttonLoading[`${request.id}_copy_reject`] ? 0.7 : 1,
                                  cursor: buttonLoading[`${request.id}_copy_reject`] ? 'not-allowed' : 'pointer'
                                }}
                              >
                                {buttonLoading[`${request.id}_copy_reject`] ? (
                                  <>
                                    <span className="copy-trading-spinner" style={{
                                      display: 'inline-block',
                                      width: '12px',
                                      height: '12px',
                                      border: '2px solid #ffffff',
                                      borderTop: '2px solid transparent',
                                      borderRadius: '50%',
                                      animation: 'spin 1s linear infinite',
                                      marginRight: '5px'
                                    }}></span>
                                    Rejecting...
                                  </>
                                ) : (
                                  "Reject"
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                          No PAMM Withdrawal found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
     
    </div>
  );
}

function StatCard({ title, value, icon, trend, trendType, onClick }) {
  return (
    <div 
      className="stat-card bg-gradient-to-br from-white via-pink-50 to-pink-100 backdrop-blur-md border border-pink-200/30 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="stat-card-header">
        <div className="stat-icon text-pink-600 bg-pink-100/50 rounded-full p-3 shadow-md">
          {icon}
        </div>
        <div className="stat-trend">
          <span className={`trend ${trendType}`}>
            {trend}
          </span>
        </div>
      </div>
      <div className="stat-content">
        <h3 className="stat-value text-2xl font-bold text-gray-800 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">{value}</h3>
        <p className="stat-title text-gray-600 font-medium">{title}</p>
      </div>
    </div>
  );
}
